/**
 * State export/import as JSON files.
 */

import type { Hex } from "viem";

/** Export file format version. */
const EXPORT_VERSION = 1;

/** Exported state file structure. */
export interface ExportedState {
  version: number;
  exportedAt: string;
  chainId: number;
  blockNumber: number;
  mode: string;
  deployment: unknown;
  tevmState: Hex;
}

/**
 * Create an export Blob from the current state.
 */
export function exportState(
  tevmState: Hex,
  deployment: unknown,
  metadata: { chainId: number; blockNumber: number; mode: string },
): Blob {
  const data: ExportedState = {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    chainId: metadata.chainId,
    blockNumber: metadata.blockNumber,
    mode: metadata.mode,
    deployment,
    tevmState,
  };

  return new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
}

/**
 * Trigger a file download of the exported state.
 */
export function downloadState(blob: Blob, filename?: string): void {
  const name =
    filename ??
    `webenv-state-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Import state from a JSON file.
 * Validates the version and returns the parsed state.
 */
export async function importState(
  file: File,
): Promise<{ deployment: unknown; tevmState: Hex }> {
  const text = await file.text();
  const data = JSON.parse(text) as ExportedState;

  if (data.version !== EXPORT_VERSION) {
    throw new Error(
      `Unsupported export version ${String(data.version)} (expected ${String(EXPORT_VERSION)})`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!data.tevmState || !data.deployment) {
    throw new Error("Invalid export file: missing state or deployment data");
  }

  return {
    deployment: data.deployment,
    tevmState: data.tevmState,
  };
}
